import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../assets/NotificationPreferences.css';

// Use production URL if on production domain, otherwise use env var or localhost
const getApiBaseUrl = () => {
  if (window.location.hostname === 'portal.jjtutorialsg.com') {
    return 'https://jjt-tutor-portal.onrender.com/api';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
};

const API_BASE_URL = getApiBaseUrl();

const NotificationPreferences = () => {
  const [preferences, setPreferences] = useState({
    rateUpdates: { inApp: true, email: true },
    newTutees: { inApp: true, email: false },
    lessonSubmissions: { inApp: true, email: false },
    endorsementReminders: { inApp: true, email: true },
    tuteeInvoiceNotifications: { inApp: true, email: true }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Get user role from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user.role || 'admin';

  // Notification type configurations with descriptions
  const notificationTypes = {
    admin: [
      {
        key: 'rateUpdates',
        title: 'Tutor Rate Updates',
        description: 'Get notified when tutor payment rates are updated for subject enrollments'
      },
      {
        key: 'newTutees',
        title: 'New Tutee Enrollments',
        description: 'Receive alerts when new tutees are added to the system'
      },
      {
        key: 'lessonSubmissions',
        title: 'Lesson Submissions',
        description: 'Be informed when tutors submit lesson attendance records'
      },
      {
        key: 'tuteeInvoiceNotifications',
        title: 'Tutee Invoice Notifications',
        description: 'Receive notifications about tutee invoice events, disputes, and payments'
      }
    ],
    tutor: [
      {
        key: 'endorsementReminders',
        title: 'Endorsement Reminders',
        description: 'Receive reminders to review and endorse your monthly lesson summaries'
      },
      {
        key: 'lessonSubmissions',
        title: 'Lesson Confirmations',
        description: 'Get confirmation notifications when you submit lesson attendance'
      },
      {
        key: 'tuteeInvoiceNotifications',
        title: 'Tutee Invoice Assignments',
        description: 'Get notified when tutee invoices are assigned to you as Finance IC'
      }
    ]
  };

  // Fetch current preferences on mount
  useEffect(() => {
    fetchPreferences();
  }, []);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/users/preferences/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setPreferences(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching preferences:', err);
      setError('Failed to load notification preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (category, channel) => {
    setPreferences(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [channel]: !prev[category][channel]
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/users/preferences/notifications`,
        { notificationPreferences: preferences },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccessMessage('Notification preferences saved successfully!');
      }
    } catch (err) {
      console.error('Error saving preferences:', err);
      setError(err.response?.data?.message || 'Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Get notification types based on user role
  const relevantNotifications = notificationTypes[userRole] || notificationTypes.admin;

  if (loading) {
    return (
      <div className="notification-preferences">
        <div className="preferences-loading">
          <div className="loading-spinner"></div>
          <p>Loading preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notification-preferences">
      <div className="preferences-header">
        <h2>Notification Preferences</h2>
        <p className="preferences-subtitle">
          Manage how you receive notifications about important events
        </p>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>
          <span>{successMessage}</span>
        </div>
      )}

      <div className="preferences-content">
        <div className="preferences-section">
          <h3 className="section-title">
            {userRole === 'admin' ? 'Admin Notifications' : 'Tutor Notifications'}
          </h3>
          <p className="section-description">
            Choose how you want to be notified for each type of event
          </p>

          <div className="preferences-list">
            {relevantNotifications.map(notif => (
              <div key={notif.key} className="preference-item">
                <div className="preference-info">
                  <h4 className="preference-title">{notif.title}</h4>
                  <p className="preference-description">{notif.description}</p>
                </div>
                
                <div className="preference-toggles">
                  <div className="toggle-group">
                    <label className="toggle-label">
                      <span className="toggle-text">In-App</span>
                      <button
                        type="button"
                        className={`toggle-switch ${preferences[notif.key]?.inApp ? 'active' : ''}`}
                        onClick={() => handleToggle(notif.key, 'inApp')}
                        aria-label={`Toggle in-app notifications for ${notif.title}`}
                      >
                        <span className="toggle-slider"></span>
                      </button>
                    </label>
                  </div>

                  <div className="toggle-group">
                    <label className="toggle-label">
                      <span className="toggle-text">Email</span>
                      <button
                        type="button"
                        className={`toggle-switch ${preferences[notif.key]?.email ? 'active' : ''}`}
                        onClick={() => handleToggle(notif.key, 'email')}
                        aria-label={`Toggle email notifications for ${notif.title}`}
                      >
                        <span className="toggle-slider"></span>
                      </button>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="preferences-info-box">
          <div className="info-icon">ℹ️</div>
          <div className="info-content">
            <h4>About Notifications</h4>
            <ul>
              <li><strong>In-App:</strong> Notifications appear in your notification center within the portal</li>
              <li><strong>Email:</strong> Notifications are sent to your registered email address</li>
              <li>You can enable both channels to ensure you never miss important updates</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="preferences-footer">
        <button
          className="btn-save"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="btn-spinner"></span>
              Saving...
            </>
          ) : (
            'Save Preferences'
          )}
        </button>
      </div>
    </div>
  );
};

export default NotificationPreferences;
